import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { GhostButton } from '@ui/buttons';
import { seriesLineCss } from './seriesLineCss';
import { EM_DASH, formatScore, formatScoreDelta, formatUtcDate, scoreDeltaToken } from './format';
import { describeSeries, MIN_PLOTTABLE, SPARK_BOX } from './scoreSeries';

const cellLayout = `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 2px 4px;
  text-align: left;
`;

// A raw <button> for the reason MemberMarkerCell gives for its own: the shared buttons render a
// <button> with no `type`, and a CTA treatment would be wrong on a grid cell.
// Inset ring (-1): this fills a grid cell, so an outside ring would sit over its neighbour.
const CellButton = styled(GhostButton)`
  ${cellLayout};

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  &:focus-visible {
    outline-offset: -1px;
  }
`;

// The same cell with nothing behind it. MemberMarkerCell makes the same split for the same reason:
// something that looks pressable but does nothing invites repeated pressing, so the control exists
// only once there is somewhere for it to go.
const StaticCell = styled.span`
  ${cellLayout};
`;

const Shell = ({ onPress, children, ...rest }) =>
  onPress ? (
    <CellButton onClick={onPress} {...rest}>
      {children}
    </CellButton>
  ) : (
    <StaticCell {...rest}>{children}</StaticCell>
  );

// The line is ink rather than a hue. A single series has no identity to encode — the row beside it
// is already carrying the numbers — and a coloured line here would compete with the Change column,
// which is where this table already says which way a score went.
const Plot = styled.svg`
  display: block;
  overflow: visible;
  flex: none;

  ${seriesLineCss}

  /* This row's own occasion, in the per-occasion grain. Hollow, because it marks a position on the
     company's line rather than adding a value to it. The cell's own — a panel has no current row. */
  .series-here {
    fill: ${({ theme }) => theme.themeColors.componentNoOpacity};
    stroke: ${({ theme }) => theme.themeColors.text};
    stroke-width: 1.75;
  }
`;

const NetText = styled(Body7)`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// Only the geometry, and only primitives beside it, so the memo actually holds: `plot` is built once
// per company per read (buildMovementIndex), and everything that varies per ROW is a number or a
// string. An object built inline here would bust the comparison on every render and the memo would
// be decoration.
const Sparkline = React.memo(({ plot, token, mark, markX, markY }) => (
  <Plot
    $dense
    width={SPARK_BOX.width}
    height={SPARK_BOX.height}
    viewBox={`0 0 ${SPARK_BOX.width} ${SPARK_BOX.height}`}
    aria-hidden="true"
    focusable="false"
    data-testid="score-sparkline"
  >
    {/* One element each, not one per gap and one per segment: at a thousand rows a page that is the
        difference between a four-figure DOM and a five-figure one. A subpath per segment is still a
        break per gap — see scoreSeries' pathD. */}
    {plot.gapD && <path className="series-gap" data-testid="spark-gap" d={plot.gapD} />}
    {plot.pathD && <path className="series-line" data-testid="spark-segment" d={plot.pathD} />}
    {mark === 'end' && plot.last && (
      <circle
        className="series-point"
        data-testid="spark-end"
        data-token={token || 'none'}
        cx={plot.last.x}
        cy={plot.last.y}
        r={3}
      />
    )}
    {mark === 'here' && <circle className="series-here" data-testid="spark-here" cx={markX} cy={markY} r={3} />}
    {mark === 'here-gap' && (
      <line
        className="series-gap"
        data-testid="spark-here-gap"
        strokeWidth={2}
        x1={markX}
        x2={markX}
        y1={SPARK_BOX.padding - 2}
        y2={SPARK_BOX.height - SPARK_BOX.padding + 2}
      />
    )}
  </Plot>
));

// The Movement cell: a company's score across the occasions of this read.
//
// It draws in BOTH grains, and the mark is what changes. Averaged, the row IS the company, so the
// terminal dot sits on the last scored occasion and wears the direction of the net change — the
// movement aggregateByCompany has to null out of Prev score and Change, since an average has no
// single occasion to have come after. Per occasion, the line is still the company's whole series and
// the hollow dot marks THIS row's place on it, which is a question the row could not answer before.
//
// Drawing in both is also what keeps the grid's shape stable across the toggle: a table that gains
// and loses a column under the reader is harder to read than one that doesn't — the same rule that
// put the occasion count inside the timestamp cell rather than in a column of its own.
export const MovementCell = ({ entry, row, domains, aggregated = false, onOpenHistory }) => {
  // No company id, or no parseable stamp on any of its rows. There is nothing to group by and
  // nothing to place a point on, so the cell says nothing rather than drawing a line for a company
  // it can't name.
  if (!entry || !entry.series.rows.length) {
    return (
      <Body7 isLowContrast title="This row has no company id, so it has no series to draw.">
        {EM_DASH}
      </Body7>
    );
  }

  const { series, plot } = entry;
  const label = row.symbol || row.companyName || `Company ${row.companyId}`;
  const description = describeSeries(series, { currentRow: aggregated ? null : row });
  const scale =
    series.scoredCount >= MIN_PLOTTABLE
      ? ` Scores ${formatScore(domains.y0)} to ${formatScore(
          domains.y1
        )}, one scale shared by every row. The line covers the whole read, ${formatUtcDate(
          series.from
        )} → ${formatUtcDate(series.to)}, and no filter narrows it.`
      : '';

  const open = onOpenHistory ? () => onOpenHistory(series.companyId) : undefined;
  const action = onOpenHistory ? ' Open this company’s score history.' : '';

  // Under two scored occasions there is no shape to draw. The count is the honest cell: a lone dot
  // in a 26px box is a mark whose meaning nobody can read, and an empty cell would claim the company
  // has no occasions at all.
  if (series.scoredCount < MIN_PLOTTABLE) {
    const text = series.scoredCount === 1 ? '1 occasion' : 'no scored occasion';
    return (
      <Shell
        onPress={open}
        title={`${description}${action}`}
        aria-label={`Score movement for ${label}. ${description}${action}`}
        data-testid="movement-cell"
        data-company-id={series.companyId}
      >
        <Body7 isLowContrast>{text}</Body7>
      </Shell>
    );
  }

  const token = scoreDeltaToken(series.net);
  // Placed from the ROW's own instant and its own score rather than by looking itself up in the
  // plotted points. Two rows can share an instant — the endpoint's uniqueness claim is unenforced —
  // and the plot keeps one point for that instant; a row that lost that tie still deserves its mark
  // where it actually sits, not the winner's.
  const markInstant = new Date(row.knowableAt).getTime();
  const hasScore = typeof row.score === 'number' && isFinite(row.score);
  const mark = aggregated ? 'end' : isNaN(markInstant) ? 'none' : hasScore ? 'here' : 'here-gap';

  return (
    <Shell
      onPress={open}
      title={`${description}${scale}${action}`}
      aria-label={`Score movement for ${label}. ${description}${scale}${action}`}
      data-testid="movement-cell"
      data-company-id={series.companyId}
      data-direction={token || 'flat'}
    >
      <Sparkline
        plot={plot}
        token={token}
        mark={mark}
        markX={mark === 'none' ? 0 : plot.x(markInstant)}
        markY={hasScore ? plot.y(row.score) : 0}
      />
      {/* The net change in digits as well as in the dot's colour. A direction that exists only as a
          colour is unreadable to a screen reader and to anyone who can't tell this green from this
          red — the rule the Change column's data-direction already states. */}
      {aggregated && (
        <NetText bold data-testid="movement-net">
          {formatScoreDelta(series.net)}
        </NetText>
      )}
    </Shell>
  );
};
